import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Page from '../../../componentes/plantilla/Page';
import Card from '../../../componentes/contenedores/Card';
import { PlanillaServicio } from '../../../servicios/planillaServicio';
import { mostraAlerta, mostraAlertaOk, mostraAlertaPregunta } from '../../../componentes/alerts/sweetAlert';

const HomePlanillas = () => {
  const navigate = useNavigate();
  const [periodos, setPeriodos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('TODOS');
  const [filtroTipo, setFiltroTipo] = useState('TODOS');
  const [busqueda, setBusqueda] = useState('');

  const datosPage = {
    titulo: {
      titulo: 'Gestión de Planillas y Nómina',
      url: '/app/admin/planillas',
      tituloUrl: 'Planillas',
      nombreUrl: 'Inicio',
    },
  };

  const cargarPeriodos = async () => {
    try {
      setCargando(true);
      const data = await PlanillaServicio.listarPeriodos();
      setPeriodos(data);
    } catch (error) {
      mostraAlerta('Error al cargar la lista de planillas', 'error');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarPeriodos();
  }, []);

  const handleAprobar = (id, descripcion) => {
    mostraAlertaPregunta(
      async (confirmado) => {
        if (confirmado) {
          try {
            await PlanillaServicio.aprobarPlanilla(id);
            mostraAlertaOk(`La planilla "${descripcion}" fue aprobada con éxito`);
            cargarPeriodos();
          } catch (error) {
            mostraAlerta('Error al aprobar la planilla', 'error');
          }
        }
      },
      `¿Desea aprobar y cerrar la planilla "${descripcion}"? Se aplicarán las amortizaciones de préstamos.`
    );
  };

  const handlePagar = (id, descripcion) => {
    mostraAlertaPregunta(
      async (confirmado) => {
        if (confirmado) {
          try {
            await PlanillaServicio.pagarPlanilla(id);
            mostraAlertaOk(`La planilla "${descripcion}" ha sido marcada como Pagada`);
            cargarPeriodos();
          } catch (error) {
            mostraAlerta('Error al marcar pago', 'error');
          }
        }
      },
      `¿Confirmar que la planilla "${descripcion}" fue cancelada a todos los colaboradores?`
    );
  };

  const handleEliminar = (id, descripcion) => {
    mostraAlertaPregunta(
      async (confirmado) => {
        if (confirmado) {
          try {
            await PlanillaServicio.eliminarPeriodo(id);
            mostraAlertaOk('Borrador de planilla eliminado');
            cargarPeriodos();
          } catch (error) {
            mostraAlerta('No se puede eliminar la planilla seleccionada', 'error');
          }
        }
      },
      `¿Eliminar definitivamente el borrador "${descripcion}"?`
    );
  };

  // Filtrado de planillas
  const periodosFiltrados = periodos.filter((p) => {
    const cumpleEstado = filtroEstado === 'TODOS' || p.estado === filtroEstado;
    const cumpleTipo = filtroTipo === 'TODOS' || p.tipoPeriodo === filtroTipo;
    const cumpleBusqueda =
      busqueda === '' ||
      p.descripcion.toLowerCase().includes(busqueda.toLowerCase()) ||
      p.codigo.toLowerCase().includes(busqueda.toLowerCase());
    return cumpleEstado && cumpleTipo && cumpleBusqueda;
  });

  // Métricas agregadas
  const totalPagado = periodos
    .filter((p) => p.estado === 'PAGADA' || p.estado === 'APROBADA')
    .reduce((acc, p) => acc + (p.totalNeto || 0), 0);

  const totalComisiones = periodos
    .reduce((acc, p) => acc + (p.totalComisiones || 0), 0);

  const borradoresPendientes = periodos.filter((p) => p.estado === 'BORRADOR').length;

  const formatLempiras = (val) =>
    `L ${Number(val || 0).toLocaleString('es-HN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const getBadgeEstado = (estado) => {
    switch (estado) {
      case 'APROBADA':
        return <span className="badge badge-success px-2 py-1"><i className="fas fa-check-circle mr-1"></i> Aprobada</span>;
      case 'PAGADA':
        return <span className="badge badge-primary px-2 py-1"><i className="fas fa-money-bill-wave mr-1"></i> Pagada</span>;
      case 'BORRADOR':
        return <span className="badge badge-warning px-2 py-1 text-dark"><i className="fas fa-edit mr-1"></i> Borrador</span>;
      default:
        return <span className="badge badge-secondary px-2 py-1">{estado}</span>;
    }
  };

  return (
    <Page datos={datosPage}>
      {/* Botones de acción rápida y navegación del módulo */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <div className="d-flex align-items-center gap-2">
          <Link to="/app/admin/planillas/generar" className="btn btn-primary shadow-sm font-weight-bold">
            <i className="fas fa-plus-circle mr-2"></i> Generar Nueva Planilla
          </Link>
          <Link to="/app/admin/planillas/configuracion" className="btn btn-outline-primary shadow-sm">
            <i className="fas fa-user-cog mr-2"></i> Configuración Salarial
          </Link>
          <Link to="/app/admin/planillas/prestamos" className="btn btn-outline-secondary shadow-sm">
            <i className="fas fa-hand-holding-usd mr-2"></i> Préstamos y Anticipos
          </Link>
        </div>
        <button className="btn btn-outline-primary shadow-sm" onClick={cargarPeriodos} title="Recargar">
          <i className="fas fa-sync-alt mr-1"></i> Actualizar
        </button>
      </div>

      {/* Tarjetas de Resumen KPI */}
      <div className="row mb-4">
        <div className="col-lg-3 col-sm-6 mb-3">
          <div className="small-box border shadow-sm rounded p-3 mb-0">
            <div className="inner">
              <span className="text-muted text-uppercase font-weight-bold" style={{ fontSize: '0.75rem' }}>
                Total Planillas Acumulado
              </span>
              <h3 className="text-primary font-weight-bold my-1" style={{ fontSize: '1.45rem' }}>
                {formatLempiras(totalPagado)}
              </h3>
              <p className="text-muted mb-0 small">Aprobadas y Pagadas</p>
            </div>
            <div className="icon text-primary" style={{ opacity: 0.15, position: 'absolute', right: '15px', top: '15px', fontSize: '3rem' }}>
              <i className="fas fa-wallet"></i>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-sm-6 mb-3">
          <div className="small-box border shadow-sm rounded p-3 mb-0">
            <div className="inner">
              <span className="text-muted text-uppercase font-weight-bold" style={{ fontSize: '0.75rem' }}>
                Comisiones por Ventas
              </span>
              <h3 className="text-success font-weight-bold my-1" style={{ fontSize: '1.45rem' }}>
                {formatLempiras(totalComisiones)}
              </h3>
              <p className="text-muted mb-0 small">Incentivos liquidados</p>
            </div>
            <div className="icon text-success" style={{ opacity: 0.15, position: 'absolute', right: '15px', top: '15px', fontSize: '3rem' }}>
              <i className="fas fa-chart-line"></i>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-sm-6 mb-3">
          <div className="small-box border shadow-sm rounded p-3 mb-0">
            <div className="inner">
              <span className="text-muted text-uppercase font-weight-bold" style={{ fontSize: '0.75rem' }}>
                Planillas en Borrador
              </span>
              <h3 className="text-warning font-weight-bold my-1" style={{ fontSize: '1.45rem' }}>
                {borradoresPendientes}
              </h3>
              <p className="text-muted mb-0 small">Pendientes de aprobación</p>
            </div>
            <div className="icon text-warning" style={{ opacity: 0.15, position: 'absolute', right: '15px', top: '15px', fontSize: '3rem' }}>
              <i className="fas fa-file-invoice-dollar"></i>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-sm-6 mb-3">
          <div className="small-box border shadow-sm rounded p-3 mb-0">
            <div className="inner">
              <span className="text-muted text-uppercase font-weight-bold" style={{ fontSize: '0.75rem' }}>
                Períodos Registrados
              </span>
              <h3 className="text-secondary font-weight-bold my-1" style={{ fontSize: '1.45rem' }}>
                {periodos.length}
              </h3>
              <p className="text-muted mb-0 small">Historial completo</p>
            </div>
            <div className="icon text-secondary" style={{ opacity: 0.15, position: 'absolute', right: '15px', top: '15px', fontSize: '3rem' }}>
              <i className="fas fa-calendar-alt"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Contenedor Principal: Tabla y Filtros */}
      <Card titulo="Historial de Períodos de Planilla">
        {/* Barra de Búsqueda y Filtros */}
        <div className="row mb-3 align-items-center">
          <div className="col-md-5 mb-2 mb-md-0">
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text border-right-0">
                  <i className="fas fa-search text-muted"></i>
                </span>
              </div>
              <input
                type="text"
                className="form-control border-left-0"
                placeholder="Buscar por código o descripción..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
          </div>

          <div className="col-md-3 col-sm-6 mb-2 mb-md-0">
            <select
              className="form-control"
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
            >
              <option value="TODOS">Todos los Estados</option>
              <option value="BORRADOR">Borrador</option>
              <option value="APROBADA">Aprobada</option>
              <option value="PAGADA">Pagada</option>
            </select>
          </div>

          <div className="col-md-3 col-sm-6 mb-2 mb-md-0">
            <select
              className="form-control"
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
            >
              <option value="TODOS">Todas las Frecuencias</option>
              <option value="QUINCENAL">Quincenal</option>
              <option value="MENSUAL">Mensual</option>
            </select>
          </div>

          <div className="col-md-1 text-right">
            {(filtroEstado !== 'TODOS' || filtroTipo !== 'TODOS' || busqueda !== '') && (
              <button
                className="btn btn-sm btn-link text-danger"
                onClick={() => {
                  setFiltroEstado('TODOS');
                  setFiltroTipo('TODOS');
                  setBusqueda('');
                }}
                title="Limpiar filtros"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Tabla de Planillas */}
        {cargando ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Cargando...</span>
            </div>
            <p className="text-muted mt-2">Cargando nóminas...</p>
          </div>
        ) : periodosFiltrados.length === 0 ? (
          <div className="text-center py-5 border rounded">
            <i className="fas fa-file-invoice text-muted" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 font-weight-bold">No se encontraron períodos de planilla</h5>
            <p className="text-muted mb-3">No existen registros que coincidan con los criterios seleccionados.</p>
            <Link to="/app/admin/planillas/generar" className="btn btn-primary font-weight-bold">
              <i className="fas fa-plus mr-1"></i> Generar la primera planilla
            </Link>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descripción del Período</th>
                  <th>Rango de Fechas</th>
                  <th>Tipo</th>
                  <th className="text-center">Colaboradores</th>
                  <th className="text-right">Comisiones</th>
                  <th className="text-right">Total Neto</th>
                  <th className="text-center">Estado</th>
                  <th className="text-center" style={{ minWidth: '160px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {periodosFiltrados.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <span className="font-weight-bold font-monospace">
                        {item.codigo}
                      </span>
                    </td>
                    <td>
                      <div className="font-weight-bold">{item.descripcion}</div>
                      <small className="text-muted">Creada el {item.fechaCreacion}</small>
                    </td>
                    <td>
                      <small className="d-block text-muted">
                        <i className="fas fa-calendar-alt mr-1"></i> {item.fechaInicio} al {item.fechaFin}
                      </small>
                    </td>
                    <td>
                      <span className="badge badge-secondary">
                        {item.tipoPeriodo}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="badge badge-secondary badge-pill px-2 py-1">
                        <i className="fas fa-users mr-1"></i> {item.totalEmpleados}
                      </span>
                    </td>
                    <td className="text-right text-success font-weight-bold">
                      {formatLempiras(item.totalComisiones)}
                    </td>
                    <td className="text-right font-weight-bold text-primary" style={{ fontSize: '1rem' }}>
                      {formatLempiras(item.totalNeto)}
                    </td>
                    <td className="text-center">
                      {getBadgeEstado(item.estado)}
                    </td>
                    <td className="text-center">
                      <div className="btn-group btn-group-sm">
                        <button
                          type="button"
                          className="btn btn-info"
                          title="Ver Detalle y Recibos"
                          onClick={() => navigate(`/app/admin/planillas/detalle/${item.id}`)}
                        >
                          <i className="fas fa-eye"></i> Detalle
                        </button>

                        {item.estado === 'BORRADOR' && (
                          <>
                            <button
                              type="button"
                              className="btn btn-success"
                              title="Aprobar Planilla"
                              onClick={() => handleAprobar(item.id, item.descripcion)}
                            >
                              <i className="fas fa-check"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              title="Eliminar Borrador"
                              onClick={() => handleEliminar(item.id, item.descripcion)}
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </>
                        )}

                        {item.estado === 'APROBADA' && (
                          <button
                            type="button"
                            className="btn btn-primary"
                            title="Marcar como Pagada"
                            onClick={() => handlePagar(item.id, item.descripcion)}
                          >
                            <i className="fas fa-dollar-sign"></i> Pagar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Page>
  );
};

export default HomePlanillas;
